import { getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { doc } from 'firebase/firestore';
import { ref, getMetadata } from 'firebase/storage';
import { db, storage } from '../../../firebase-config';
import { AI_PIPELINE_SCHEMA_V2 } from '../../../constants/aiPipelineModes';
import {
  buildClassifiedEvidenceFiles,
  countEvidenceJobStatuses,
} from './evidenceFileClassify';
import { evidenceAnalysisJobRef } from './schoolSchemaPaths';

/**
 * Fill size / contentType from Storage when vault rows lack them
 * (needed for safe duplicate detection).
 */
async function enrichVaultFilesFromStorage(schoolId, vault) {
  const list = Array.isArray(vault) ? vault : [];
  const out = [];
  for (const file of list) {
    const hasSize = file.size != null && file.size !== '';
    const hasType = !!(file.contentType || file.type || file.mimeType);
    if (hasSize && hasType) {
      out.push(file);
      continue;
    }
    try {
      const meta = await getMetadata(ref(storage, `evidence/${schoolId}/${file.name}`));
      out.push({
        ...file,
        size: hasSize ? Number(file.size) : Number(meta.size),
        contentType:
          file.contentType || file.type || file.mimeType || meta.contentType || null,
      });
    } catch (err) {
      console.warn('evidence metadata enrich failed', schoolId, file.name, err?.message);
      out.push(file);
    }
  }
  return out;
}

/**
 * Create/replace the current v2 evidence analysis job for a school.
 * First pass: photos skipped, duplicates (name+type+size) ignored, rest queued.
 */
export async function startEvidenceAnalysisJob(schoolId, actor = {}) {
  const auditSnap = await getDoc(doc(db, 'audits', schoolId));
  const vault = auditSnap.exists() ? auditSnap.data()?.evidenceVault || [] : [];
  const enriched = await enrichVaultFilesFromStorage(schoolId, vault);
  const files = buildClassifiedEvidenceFiles(enriched);
  const counts = countEvidenceJobStatuses(files);

  const noteParts = [];
  if (counts.queued > 0) {
    noteParts.push(
      `Registration schema seeded. ${counts.queued} document(s) queued for analysis.`
    );
  } else if (counts.total === 0) {
    noteParts.push('No evidence files in vault. Registration schema seeded only.');
  } else {
    noteParts.push('No eligible documents to analyse after first-pass filters.');
  }
  if (counts.skippedPhoto > 0) {
    noteParts.push(`${counts.skippedPhoto} photo(s) skipped for safeguarding.`);
  }
  if (counts.skippedVideo > 0) {
    noteParts.push(`${counts.skippedVideo} video(s) skipped (not analysed by AI).`);
  }
  if (counts.skippedDuplicate > 0) {
    noteParts.push(
      `${counts.skippedDuplicate} duplicate(s) ignored (same name, type, and size).`
    );
  }

  const payload = {
    schoolId,
    pipeline: AI_PIPELINE_SCHEMA_V2,
    status: counts.queued > 0 ? 'running' : 'complete',
    phase: 'v2a_seed_and_queue',
    total: counts.total,
    done: counts.done,
    failed: counts.failed,
    skipped: counts.skipped,
    skippedPhoto: counts.skippedPhoto,
    skippedVideo: counts.skippedVideo,
    skippedDuplicate: counts.skippedDuplicate,
    queued: counts.queued,
    extracted: counts.extracted,
    files,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: actor.uid || actor.email || 'staff',
    note: noteParts.join(' '),
  };

  await setDoc(evidenceAnalysisJobRef(schoolId), payload);
  return payload;
}

export async function getEvidenceAnalysisJob(schoolId) {
  const snap = await getDoc(evidenceAnalysisJobRef(schoolId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}
